import {EventEmitter, Injectable} from '@angular/core';
import {BehaviorSubject, catchError, map, Observable, of, take, tap, timer} from "rxjs";
import {Melody} from "../model/melody";
import {AuthService} from "./auth.service";
import {ModalsService} from "./modals.service";
import {HttpClient, HttpParams} from "@angular/common/http";
import {Subscriber} from "../model/subscriber";
import {ContentService} from "./content.service";
import {SubscriberMelody} from "../model/subscriber-melody";
import {updateSubscriberMelodiesPaths} from "../utils/paths-utils";
import {ContentStatus} from "../model/content-status";
import {RulesService} from "./rules.service";
import {SubscriptionStatus} from "../model/subscription-status";
import {Router} from "@angular/router";
import {MetrikaService} from "./metrika.service";
import {switchMap} from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class SubscriberService {
  private readonly _msisdn: BehaviorSubject<string>;
  private readonly _subscriber: BehaviorSubject<Subscriber>;
  private readonly _favoriteMelodiesIds: BehaviorSubject<ReadonlyArray<number>>;
  private readonly _subscriberMelodies: BehaviorSubject<ReadonlyArray<SubscriberMelody>>;
  private readonly _subscriberDataLoaded: EventEmitter<void>;
  error = false;

  constructor(private readonly authService: AuthService,
              private readonly modalsService: ModalsService,
              private readonly contentService: ContentService,
              private readonly rulesService: RulesService,
              private readonly httpClient: HttpClient,
              private metrikaService: MetrikaService,
              private router: Router) {
    this._msisdn = new BehaviorSubject<string>(null);
    this._subscriber = new BehaviorSubject<Subscriber>(null);
    this._favoriteMelodiesIds = new BehaviorSubject<ReadonlyArray<number>>([]);
    this._subscriberMelodies = new BehaviorSubject<ReadonlyArray<SubscriberMelody>>([]);
    this._subscriberDataLoaded = new EventEmitter<void>();

    this.authService.authStateChanged.subscribe(loggedIn => {
      if (loggedIn) {
        this.loadSubscriberData(this.authService.msisdn);
      } else {
        this.clearSubscriber();
        this.clearSubscriberMelodies()
      }
    });
  }

  get msisdn(): Observable<string> {
    return this._msisdn.asObservable();
  }

  getCurrentMsisdn(): string {
    return this._msisdn.value;
  }

  get subscriber(): Observable<Subscriber> {
    return this._subscriber.asObservable();
  }

  get subscriberValue(): Subscriber {
    return this._subscriber.value;
  }

  get favoriteMelodiesIds(): Observable<ReadonlyArray<number>> {
    return this._favoriteMelodiesIds.asObservable();
  }

  get subscriberMelodies(): Observable<ReadonlyArray<SubscriberMelody>> {
    return this._subscriberMelodies.asObservable();
  }

  get subscriberMelodiesValue(): ReadonlyArray<SubscriberMelody> {
    return this._subscriberMelodies.value;
  }

  clearSubscriberMelodies() {
    this._subscriberMelodies.next([]);
  }

  toggleFavorite(melody: Melody): void {
    if (this.authService.authenticated) {
      this.setFavorite(melody, !melody.favorite);
    } else {
      this.modalsService.showLogin(() => {
        this.setFavorite(melody, true);
      });
    }
  }

  purchaseMelodyPopup(melody: Melody): void {
    if (this.authService.authenticated) {
      this.modalsService.showPurchase(melody);
    } else {
      this.modalsService.showLogin(() => {
        this._subscriberDataLoaded
          .pipe(take(1))
          .subscribe(() => {
            this.modalsService.showPurchase(melody);
          });
      });
    }
  }

  giftMelodyPopup(melody: Melody): void {
    if (this.authService.authenticated) {
      this.modalsService.showGift(melody);
    } else {
      this.modalsService.showLogin(() => {
        this.modalsService.showGift(melody);
      });
    }
  }

  activateSubscriptionBanner() : void {
    if (this.authService.authenticated) {
      this.metrikaService.addEvent('activate_button_click', this._msisdn.value, null)
      this.chooseSubscriptions()
    } else {
      this.modalsService.showLogin(() => {
        this._subscriberDataLoaded
          .pipe(take(1))
          .subscribe(() => {
            this.metrikaService.addEvent('activate_button_click', this._msisdn.value, null)
            this.chooseSubscriptions();
          });
      });
    }
  }

  activateSubscriptionMainPage(privetPlus: boolean) : void {
    if (this.authService.authenticated) {
      this.modalsService.showActivateSubscription(privetPlus)
    } else {
      this.modalsService.showLogin(() => {
        this._subscriberDataLoaded
          .pipe(take(1))
          .subscribe(() => {
            let privetPlusSubscription = this.subscriberValue.privetPlusStatus == SubscriptionStatus.ACTIVE || this.subscriberValue.privetPlusStatus == SubscriptionStatus.ACTIVATING;
            let privetSubscription = this.subscriberValue.privetStatus == SubscriptionStatus.ACTIVE || this.subscriberValue.privetStatus == SubscriptionStatus.ACTIVATING;
            if (privetPlus && !privetPlusSubscription) {
              this.modalsService.showActivateSubscription(privetPlus)
            }

            if (!privetPlus && !privetSubscription) {
              this.modalsService.showActivateSubscription(privetPlus)
            }
          });
      });
    }
  }

  chooseSubscriptions(): void {
    let privetPlus = this.subscriberValue.privetPlusStatus == SubscriptionStatus.ACTIVE || this.subscriberValue.privetPlusStatus == SubscriptionStatus.ACTIVATING

    if (privetPlus) {
      this.router.navigate(['/subscriber/subscriptions']);
    } else {
      this.modalsService.showChooseSubscriptions();
    }
  }

  getFavoriteMelodies(): Observable<ReadonlyArray<Melody>> {
    return this.contentService.getMelodiesList('/content/favorites');
  }

  loadSubscriberMelodies(): Observable<ReadonlyArray<SubscriberMelody>> {
    return this.httpClient.get<ReadonlyArray<SubscriberMelody>>('/rbt/melodies')
      .pipe(
        map(updateSubscriberMelodiesPaths),
        catchError(() => of(new Array<SubscriberMelody>())),
        switchMap(melodies => {
          this._subscriberMelodies.next(melodies);

          const hasActivating = melodies.filter(melody => melody.contentStatus == ContentStatus.ACTIVATING).length > 0;

          if (hasActivating) {
            return timer(5000).pipe(
              switchMap(() => this.loadSubscriberMelodies())
            );
          }

          return of(melodies);
        })
      );
  }

  purchaseMelody(melody: Melody, privetPlus: boolean, callback: (status: string) => void):void {
    this.httpClient.put<string>(`/rbt/melodies/${melody.rbtCode}`, null, {
      params: {
        privetPlus: privetPlus
      }
    }).pipe(tap(response => {
        if (response == 'SUCCESS') {
          this.metrikaService.addEvent (privetPlus ? 'add_privet_plus_melody' : 'add_privet_melody', this._msisdn.value, response, melody)
        } else {
          this.metrikaService.addEvent(privetPlus ? 'add_privet_plus_melody_error' : 'add_privet_melody_error', this._msisdn.value, response, melody)
        }

        if (response == 'TOO_MANY_MELODIES' || 'WRONG_SERVICE') {
          callback(response);
        } else {
          callback(null)
        }
      }
    )).subscribe(() => this.loadSubscriberMelodies());
  }

  giftMelody(msisdn: string, melody: Melody, privetPlusPurchase: boolean): Observable<string> {
    const params = new HttpParams()
      .set('msisdnTo', msisdn)
      .set('rbtCode', melody.rbtCode)
      .set('privetPlusPurchase', privetPlusPurchase);
    return this.httpClient.put<string>(`/rbt/melodies/gift`, null, { params })
      .pipe(tap(response => {
          this.metrikaService.addEvent(response == 'SUCCESS' ? 'gift_melody' : 'gift_melody_error', this._msisdn.value, response, melody, msisdn)
        }
      ));
  }

  checkMsisdn(msisdn: string): Observable<boolean | null> {
    const url = '/rbt/subscriptions/check/msisdn';

    return this.httpClient.get<boolean>(url, {
      params: { msisdn: msisdn }
    }).pipe(
      map((response) => {
        return response === true;
      }),
      catchError((error) => {
        return of(null);
      })
    );
  }

  getStatusesSubscriber(msisdn: string): Observable<any> {
    const url = '/rbt/subscriptions/status';

    return this.httpClient.get<any>(url, {
      params: { msisdn: msisdn }
    }).pipe(
      map((response) => {
        return response;
      }),
      catchError((error) => {
        return of(null);
      })
    );
  }

  uploadUgc(file: File, start: number, length: number): Observable<string> {
    let formData = new FormData();
    formData.append('file', file);

    return this.httpClient.post<any>('/rbt/melodies/ugc', formData, {
      params: {
        start: start,
        length: length,
      }
    }).pipe(
      catchError(response => {
        this.metrikaService.addEvent('upload_ugc_error', this._msisdn.value, response, null, null, file.name);
        return of('UNKNOWN_ERROR')
      }),
      tap(response => {
        this.metrikaService.addEvent('upload_ugc', this._msisdn.value, response, null, null, file.name);
      })
    )
  }

  deleteMelody(melody: SubscriberMelody): void {
    this.httpClient.delete<void>(`/rbt/melodies/${melody.idFromPlatform}`)
      .subscribe(() => this.loadSubscriberMelodies());
  }

  addToAutomate(melody: SubscriberMelody): void {
    this.httpClient.put<void>(`/rbt/melodies/automate/${melody.idFromPlatform}`, null)
      .subscribe(() => this.loadSubscriberMelodies());
  }

  deleteFromAutomate(melody: SubscriberMelody): void {
    this.httpClient.delete<void>(`/rbt/melodies/automate/${melody.idFromPlatform}`)
      .subscribe(() => this.loadSubscriberMelodies());
  }

  loadSubscriptions(): void {
    this.httpClient.get<Subscriber>('/rbt/subscriptions')
      .pipe(catchError(() => {
        return of(null)
      })).subscribe(subscriber => {
      this._subscriber.next(subscriber);
      this._subscriberDataLoaded.emit();
    });
  }

  activateSubscriptions(privetPlus: boolean): Observable<string> {
    return this.httpClient.put<string>('/rbt/subscriptions/' + (privetPlus ? 'privet-plus' : 'privet'), null)
      .pipe(tap(response => {
          if (response == 'SUCCESS') {
            this.metrikaService.addEvent(privetPlus ? 'activate_privet_plus' : 'activate_privet', this._msisdn.value, response)
          } else {
            this.metrikaService.addEvent(privetPlus ? 'activate_privet_plus_failed' : 'activate_privet_failed', this._msisdn.value, response)
          }
        }
      ));
  }

  deactivateSubscriptions(privetPlus: boolean): Observable<string> {
    return this.httpClient.delete<string>('/rbt/subscriptions/' + (privetPlus ? 'privet-plus' : 'privet'))
      .pipe(tap(response => {
          if (response == 'SUCCESS') {
            this.metrikaService.addEvent(privetPlus ? 'deactivate_privet_plus' : 'deactivate_privet', this._msisdn.value, response)
          } else {
            this.metrikaService.addEvent(privetPlus ? 'deactivate_privet_plus_failed' : 'deactivate_privet_failed',this._msisdn.value, response)
          }
        }
      ));
  }

  getSubscriberMelodyStatus(melodyId: number): ContentStatus {
    let melodies = this.subscriberMelodiesValue;
    let subscriberMelody = melodies ? melodies.filter(melody => melody.id == melodyId).pop() : null;

    return subscriberMelody ? subscriberMelody.contentStatus : ContentStatus.NOT_FOUND;
  }

  public loadSubscriberData(msisdn: string): void {
    this._msisdn.next(msisdn);

    this.loadSubscriptions();
    this.loadFavoriteMelodiesIds();
    this.loadSubscriberMelodies();
    this.rulesService.loadRules();
    if(!this.metrikaService.encryptedMsisdn) {
      this.metrikaService.encryptMsisdn(msisdn);
    }
  }

  private clearSubscriber(): void {
    this.rulesService.clearRules();
    this._msisdn.next(null);
    this._favoriteMelodiesIds.next([]);
    this._subscriber.next(null);
  }

  private loadFavoriteMelodiesIds(): void {
    this.httpClient.get<Array<number>>('/content/favorites/ids')
      .pipe(
        catchError(() => {
          return of(new Array<number>())
        })).subscribe(melodies => this._favoriteMelodiesIds.next(melodies));
  }

  private setFavorite(melody: Melody, favorite: boolean): void {
    if (favorite) {
      this.addToFavorites(melody);
    } else {
      this.removeFromFavorites(melody);
    }
  }

  private addToFavorites(melody: Melody): void {
    this.httpClient.put<boolean>(`/content/favorites/${melody.id}`, null).subscribe(() => this.loadFavoriteMelodiesIds());
  }

  private removeFromFavorites(melody: Melody): void {
    this.httpClient.delete<boolean>(`/content/favorites/${melody.id}`).subscribe(() => this.loadFavoriteMelodiesIds());
  }
}
